import React, { useState, useEffect, useMemo, Ref, useRef } from 'react';
import { findIndex, memoize } from 'lodash';

type ListItemCallbackProps = {
  onClick: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
};

type ListItemProps = {
  highlighted: boolean;
} & ListItemCallbackProps;

type ListItemPropsWithRef<E extends HTMLElement> = {
  highlighted: boolean;
  ref: Ref<E> | undefined;
} & ListItemCallbackProps;

type UseSelectedListReturnType<T> = {
  selectedItemIndex: number | null;
  selectedItem: T | null;
  getListItemProps: (item: T) => ListItemProps;
};

type UseSelectedListReturnTypeWithRef<T, E extends HTMLElement> = {
  selectedItemIndex: number | null;
  selectedItem: T | null;
  getListItemProps: (item: T) => ListItemPropsWithRef<E>;
};

interface ConfigBase {
  active: boolean;
  autoScroll?: false | null | undefined;
  alwaysHovered?: boolean;
  // If provided, this will be called when enter is pressed and there is no selected item.
  onEnterFallback?: () => void;
}

interface ConfigWithAutoScroll extends Omit<ConfigBase, 'autoScroll'> {
  autoScroll: true;
}

type Config = ConfigBase | ConfigWithAutoScroll;

function isAutoScrollConfig(config: Config): config is ConfigWithAutoScroll {
  return config.autoScroll === true;
}

/**
 * A hook that provides functionality for keyboard selection for lists.
 *
 * @param items
 * @param onChange
 *
 * @example
 * function UserSelectionList({ users, onChange }) {
 *   const { getListItemProps, selectedItemIndex } = useSelectedList(users, onChange)
 *   return (
 *     <ul>
 *       {users.map((user, index) => (
 *         <li
 *           selected={index === selectedItemIndex}
 *           {...getListItemProps(i)}
 *         >
 *             {user.name}
 *         </li>
 *       ))}
 *     </ul>
 *   )
 * }
 */
export function useSelectableList<T>(
  items: ReadonlyArray<T>,
  matchItemPredicate: (maybeItem: T, item: T) => boolean,
  onChange: (item: T) => void,
  config: ConfigBase,
  onHighlight?: (item: T | null) => void,
  tabSelects?: boolean,
): UseSelectedListReturnType<T>;

export function useSelectableList<E extends HTMLElement, T>(
  items: ReadonlyArray<T>,
  matchItemPredicate: (maybeItem: T, item: T) => boolean,
  onChange: (item: T) => void,
  config: ConfigWithAutoScroll,
  onHighlight?: (item: T | null) => void,
  tabSelects?: boolean,
): UseSelectedListReturnTypeWithRef<T, E>;

export function useSelectableList<E extends HTMLElement, T>(
  items: ReadonlyArray<T>,
  matchItemPredicate: (maybeItem: T, item: T) => boolean,
  onChange: (item: T) => void,
  config: Config,
  onHighlight?: (item: T | null) => void,
  tabSelects: boolean = false,
) {
  const { active, alwaysHovered = false } = config;
  const defaultHoveredIndex = alwaysHovered ? 0 : null;
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(defaultHoveredIndex);
  const [highlightedRef, setHighlightedRef] = useState<E | null>(null);
  const isKeyboardInteractionRef = useRef(false);

  useEffect(() => {
    setHoveredOptionIndex(defaultHoveredIndex);
  }, [defaultHoveredIndex, items]);

  useEffect(() => {
    if (highlightedRef && isKeyboardInteractionRef.current) {
      requestAnimationFrame(() => {
        highlightedRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }, [highlightedRef]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (items.length > 0) {
            isKeyboardInteractionRef.current = true;
            setHoveredOptionIndex(i => (i == null ? 0 : (i + 1) % items.length));
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (items.length > 0) {
            isKeyboardInteractionRef.current = true;
            setHoveredOptionIndex(i =>
              i == null ? items.length - 1 : (i - 1 + items.length) % items.length,
            );
          }
          break;
        case 'Tab':
        // fallthrough
        case 'Enter': {
          if (e.key === 'Tab' && !tabSelects) {
            break;
          }
          const selectedItem = hoveredOptionIndex != null ? items[hoveredOptionIndex] : null;
          if (selectedItem != null) {
            e.preventDefault();
            onChange(selectedItem);
          } else {
            config.onEnterFallback?.();
          }
          break;
        }
      }
    };
    if (active) {
      window.addEventListener('keydown', handleKeyPress);
    }
    /**
     * Clear the selected item when active is false.
     */
    if (!active && hoveredOptionIndex) {
      isKeyboardInteractionRef.current = false;
      setHoveredOptionIndex(defaultHoveredIndex);
    }
    return () => {
      if (active) {
        window.removeEventListener('keydown', handleKeyPress);
      }
    };
    // TODO(jcarroll2007): I think we can improve this effect by not having it
    // be dependent on the `hoveredOptionIndex`. Because it is dependent on
    // that piece of state, every time the hovered option
    // index changes the listener is removed and re-added unnecessarily.
    // The tricky part here is handling the enter key press. I think that would require
    // a second piece of state or useEffect to manage though.
  }, [hoveredOptionIndex, active, items, onChange, defaultHoveredIndex]);

  const getListItemCallbackProps = useMemo(
    () =>
      memoize((item: T) => {
        const itemIndex = findIndex(items, maybeItem => matchItemPredicate(maybeItem, item));

        return {
          onClick: (e: React.MouseEvent) => {
            isKeyboardInteractionRef.current = false;
            setHoveredOptionIndex(itemIndex);
            onChange(item);
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          },
          onMouseMove: () => {
            isKeyboardInteractionRef.current = false;
          },
          onMouseEnter: () => {
            if (!isKeyboardInteractionRef.current) {
              setHoveredOptionIndex(itemIndex);
            }
          },
          onMouseLeave: () => {
            if (!alwaysHovered && !isKeyboardInteractionRef.current) setHoveredOptionIndex(null);
          },
        };
      }),
    [items, onChange, alwaysHovered],
  );

  const selectedItem = hoveredOptionIndex != null ? items[hoveredOptionIndex] : null;
  useEffect(() => {
    if (selectedItem != null) {
      onHighlight?.(selectedItem);
    }
  }, [onHighlight, selectedItem]);

  return {
    selectedItemIndex: hoveredOptionIndex,
    selectedItem,
    getListItemProps: (item: T) => {
      const itemIndex = findIndex(items, maybeItem => matchItemPredicate(maybeItem, item));
      const isHighlighted = itemIndex === hoveredOptionIndex;

      const props: ListItemProps = {
        highlighted: isHighlighted,
        ...getListItemCallbackProps(item),
      };

      /**
       * If auto scroll config option true we need to get the ref to be able to
       * scroll to the active element.
       */
      if (isAutoScrollConfig(config)) {
        return {
          ...props,
          ref: isHighlighted ? setHighlightedRef : undefined,
        } as ListItemPropsWithRef<E>;
      }

      return props;
    },
  };
}
